import {
  Box,
  HStack,
  Spinner,
  Tag,
  TagLabel,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';

import { useCompanyCompareStore } from '../hooks/useCompanyCompareStore';
import type { PayDataStatus } from '../hooks/usePayDataAvailability';
import type { CompanyValidationStatus } from '../hooks/useValidateCompanies';

export const OVERLAY_COLORS = [
  '#E8A18A',
  '#70CA97',
  '#AB99D2',
  '#F6C864',
  '#78B9D2',
  '#F28B82',
  '#81C995',
  '#A0C4FF',
  '#FFD6A5',
  '#BDB2FF',
];

/** PayV2 supports max 10 entities; 1 is the primary, leaving 9 for overlays */
export const MAX_OVERLAY_ENTITIES = 9;

export const PRIMARY_COLOR = '#4299E1';

/** Dedicated overlay color palette for globally-selected compare companies */
export const COMPARE_OVERLAY_COLORS = [
  '#9F7AEA',
  '#ED64A6',
  '#38B2AC',
  '#DD6B20',
];

export interface ChipCompany {
  rcid: number;
  name: string;
  source: 'work_history' | 'competitor';
  enabled: boolean;
  validationStatus: CompanyValidationStatus;
  /** Whether this company has unique pay data available */
  payDataStatus: PayDataStatus;
  /** Assigned overlay color (stable per chip position) */
  color: string;
}

const CompanyChip = ({
  company,
  onToggle,
  disableEnable,
  disableReason,
}: {
  company: ChipCompany;
  onToggle: (rcid: number) => void;
  disableEnable: boolean;
  disableReason?: string;
}) => {
  const isInvalid = company.validationStatus === 'invalid';
  const isNoPayData = company.payDataStatus === 'no_data';
  const isPayChecking = company.payDataStatus === 'checking';
  const isPending = company.validationStatus === 'pending';
  const isDisabled = isInvalid || isNoPayData;
  const isActive = company.enabled && !isDisabled;
  const isDisabledByLimit = disableEnable && !company.enabled;

  let bgColor = '#c7d0da';
  if (isDisabled) bgColor = '#FED7D7';
  else if (isActive) bgColor = 'white';

  let borderColor = '#A4ADBE';
  if (isDisabled) borderColor = '#FC8181';
  else if (isActive) borderColor = '#46BEF1';

  let textColor = '#7A88A2';
  if (isDisabled) textColor = '#C53030';
  else if (isActive) textColor = '#2D426A';

  let cursorStyle = 'pointer';
  if (isDisabled || isDisabledByLimit) cursorStyle = 'not-allowed';

  let opacityValue = 1;
  if (isDisabled) opacityValue = 0.8;
  else if (isPending || isPayChecking) opacityValue = 0.6;

  const chipContent = (
    <Tag
      size="sm"
      height="1.8rem"
      borderRadius="3px"
      backgroundColor={bgColor}
      border={`1px solid ${borderColor}`}
      color={textColor}
      flexShrink={0}
      cursor={cursorStyle}
      opacity={opacityValue}
      onClick={() => {
        if (isDisabled || isDisabledByLimit) return;
        onToggle(company.rcid);
      }}
      role="button"
      aria-pressed={isActive}
      data-testid={`overlay-chip-${company.rcid}`}
    >
      <TagLabel fontSize="11.5px">
        <Box
          as="span"
          display="inline-block"
          width="8px"
          height="8px"
          borderRadius="2px"
          marginRight="5px"
          verticalAlign="middle"
          background={isActive ? company.color : '#A4ADBE'}
        />
        {company.name}
        {(isPending || isPayChecking) && ' ...'}
      </TagLabel>
    </Tag>
  );

  if (isInvalid) {
    return (
      <Tooltip
        label="Company not found in Revelio data — no salary data available"
        fontSize="xs"
        hasArrow
        placement="top"
      >
        {chipContent}
      </Tooltip>
    );
  }

  if (isNoPayData) {
    return (
      <Tooltip
        label="No unique pay data available for this company"
        fontSize="xs"
        hasArrow
        placement="top"
      >
        {chipContent}
      </Tooltip>
    );
  }

  if (isDisabledByLimit) {
    return (
      <Tooltip
        label={
          disableReason ??
          `Maximum ${MAX_OVERLAY_ENTITIES} comparison companies reached`
        }
        fontSize="xs"
        hasArrow
        placement="top"
      >
        {chipContent}
      </Tooltip>
    );
  }

  return chipContent;
};

export const CompanyComparisonChips = ({
  chips,
  onToggle,
  isValidating,
  competitorsLoading,
  isCheckingPayData,
  enabledCount,
  companyToggleOn,
}: {
  chips: ChipCompany[];
  onToggle: (rcid: number) => void;
  isValidating: boolean;
  competitorsLoading: boolean;
  isCheckingPayData: boolean;
  enabledCount: number;
  companyToggleOn: boolean;
}) => {
  const workHistoryChips = chips.filter((c) => c.source === 'work_history');
  const competitorChips = chips.filter((c) => c.source === 'competitor');
  const atMax = enabledCount >= MAX_OVERLAY_ENTITIES;

  if (chips.length === 0 && !competitorsLoading && !isValidating) return null;

  const disableReason = !companyToggleOn
    ? 'Enable the Company filter to compare'
    : undefined;

  return (
    <VStack align="stretch" spacing={2}>
      <HStack spacing={3} align="center">
        <Text fontSize="xs" fontWeight="semibold" color="gray.500">
          Compare
        </Text>
        {(isValidating || competitorsLoading || isCheckingPayData) && (
          <Spinner size="xs" color="blue.400" />
        )}
        {enabledCount > 0 && (
          <Text fontSize="xs" color="gray.400">
            {enabledCount}/{MAX_OVERLAY_ENTITIES}
          </Text>
        )}
      </HStack>

      {workHistoryChips.length > 0 && (
        <HStack spacing={1} flexWrap="wrap" align="center">
          <Text fontSize="10px" color="gray.400" mr={1} flexShrink={0}>
            Previous
          </Text>
          {workHistoryChips.map((chip) => (
            <CompanyChip
              key={chip.rcid}
              company={chip}
              onToggle={onToggle}
              disableEnable={atMax || !companyToggleOn}
              disableReason={disableReason}
            />
          ))}
        </HStack>
      )}

      {competitorChips.length > 0 && (
        <HStack spacing={1} flexWrap="wrap" align="center">
          <Text fontSize="10px" color="gray.400" mr={1} flexShrink={0}>
            Competitors
          </Text>
          {competitorChips.map((chip) => (
            <CompanyChip
              key={chip.rcid}
              company={chip}
              onToggle={onToggle}
              disableEnable={atMax || !companyToggleOn}
              disableReason={disableReason}
            />
          ))}
        </HStack>
      )}

      {competitorsLoading && competitorChips.length === 0 && (
        <HStack spacing={2}>
          <Spinner size="xs" />
          <Text fontSize="xs" color="gray.400">
            Loading competitors...
          </Text>
        </HStack>
      )}
    </VStack>
  );
};

export const SelectedComparisonsChips = ({
  enabledCompareRcids,
  onToggle,
  enabledCount,
  maxOverlays,
  companyToggleOn,
}: {
  enabledCompareRcids: Set<number>;
  onToggle: (rcid: number) => void;
  enabledCount: number;
  maxOverlays: number;
  companyToggleOn: boolean;
}) => {
  const selectedCompanies = useCompanyCompareStore((s) => s.selectedCompanies);

  if (selectedCompanies.length === 0) return null;

  const atMax = enabledCount >= maxOverlays;

  return (
    <VStack align="stretch" spacing={2}>
      <HStack spacing={3} align="center">
        <Text fontSize="xs" fontWeight="semibold" color="gray.500">
          Selected Comparisons
        </Text>
      </HStack>
      <HStack spacing={1} flexWrap="wrap" align="center">
        {selectedCompanies.map((company, idx) => {
          const isActive = enabledCompareRcids.has(company.rcid);
          const isDisabledByLimit = atMax && !isActive;
          const isDisabledByToggle = !companyToggleOn;
          const chipColor =
            COMPARE_OVERLAY_COLORS[idx % COMPARE_OVERLAY_COLORS.length] ??
            '#9F7AEA';

          let bgColor = '#c7d0da';
          if (isActive) bgColor = 'white';

          let borderColor = '#A4ADBE';
          if (isActive) borderColor = chipColor;

          let textColor = '#7A88A2';
          if (isActive) textColor = '#2D426A';

          let cursorStyle = 'pointer';
          if (isDisabledByLimit || isDisabledByToggle) {
            cursorStyle = 'not-allowed';
          }

          const chipContent = (
            <Tag
              key={company.rcid}
              size="sm"
              height="1.8rem"
              borderRadius="3px"
              backgroundColor={bgColor}
              border={`1px solid ${borderColor}`}
              color={textColor}
              flexShrink={0}
              cursor={cursorStyle}
              onClick={() => {
                if (isDisabledByLimit || isDisabledByToggle) return;
                onToggle(company.rcid);
              }}
              role="button"
              aria-pressed={isActive}
              data-testid={`compare-overlay-chip-${company.rcid}`}
            >
              <TagLabel fontSize="11.5px">
                <Box
                  as="span"
                  display="inline-block"
                  width="8px"
                  height="8px"
                  borderRadius="2px"
                  marginRight="5px"
                  verticalAlign="middle"
                  background={isActive ? chipColor : '#A4ADBE'}
                />
                {company.name}
              </TagLabel>
            </Tag>
          );

          if (isDisabledByToggle) {
            return (
              <Tooltip
                key={company.rcid}
                label="Enable the Company filter to compare"
                fontSize="xs"
                hasArrow
                placement="top"
              >
                {chipContent}
              </Tooltip>
            );
          }

          if (isDisabledByLimit) {
            return (
              <Tooltip
                key={company.rcid}
                label={`Maximum ${maxOverlays} comparison companies reached`}
                fontSize="xs"
                hasArrow
                placement="top"
              >
                {chipContent}
              </Tooltip>
            );
          }

          return chipContent;
        })}
      </HStack>
    </VStack>
  );
};
